import IconTime from '@apollo/icons/default/IconTime.svg';
import { Button, ButtonGroup } from '@apollo/orbit';
import { Popover } from '@apollo/space-kit/Popover';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { insightsRouteConfig } from 'src/app/graph/routes';
import { Icon as IconBolt } from 'src/assets/icon-bolt.svg';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import {
  FastModeRange,
  TimeRangeOption,
  printTimeRangeLabel,
} from 'src/lib/config/timeRangeOptions';
import { getAbsoluteDateBounds } from 'src/lib/date/date';

import { TimeRangeFilter } from '../timeRangeFilter/TimeRangeFilter';

import { FilterButton } from './filterButton/FilterButton';
import { FilterPane } from './filterPane/FilterPane';

interface Props {
  selectedRange: TimeRangeOption;
  maxRangeInDays: number;
  ranges: ReadonlyArray<TimeRangeOption>;
  fastModeRange?: FastModeRange;
}

export const OperationInsightsFilter = ({
  maxRangeInDays,
  ranges,
  selectedRange,
  fastModeRange,
}: Props) => {
  const history = useHistory();
  const [_, setStoredView] = useLocalStorage('errorInsightsView');
  const {
    routeParams,
    defaultParams,
    hasActiveFiltersForSubset,
    setFilterParams,
    resetToDefaultLocation,
  } = useOperationInsightsFilter();

  const onRangeSelected = useCallback(
    (
      range?: TimeRangeOption,
      fromDate?: string,
      toDate?: string,
      selectedFastModeRange?: FastModeRange,
    ) =>
      setFilterParams(insightsRouteConfig, {
        range,
        from: fromDate,
        to: toDate,
        fastModeRange: selectedFastModeRange,
      }),
    [setFilterParams],
  );

  const handleResetFilters = useCallback(() => {
    // clear local storage when resetting
    setStoredView(undefined);
    history.push(resetToDefaultLocation(insightsRouteConfig));
  }, [history, resetToDefaultLocation, setStoredView]);

  const { timeFrom: absTimeFrom, timeTo: absTimeTo } = getAbsoluteDateBounds(
    selectedRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
  );

  return (
    <ButtonGroup>
      <Popover
        interactive
        placement="bottom-end"
        content={
          <FilterPane
            title="Time Range"
            key="Date Filter"
            data-testid="time-range-filter-pane"
          >
            <TimeRangeFilter
              className="w-52"
              onRangeSelected={onRangeSelected}
              ranges={ranges}
              range={selectedRange}
              from={absTimeFrom}
              to={absTimeTo}
              maxRangeInDays={maxRangeInDays}
              showFastModeRanges={true}
              fastModeRange={fastModeRange}
            />
          </FilterPane>
        }
        trigger={
          <FilterButton
            size="md"
            variant="hidden"
            leftIcon={
              fastModeRange ? (
                <IconBolt
                  weight="thin"
                  className="size-4 fill-none"
                  fill="none"
                />
              ) : (
                <IconTime />
              )
            }
          >
            {printTimeRangeLabel(selectedRange)}
          </FilterButton>
        }
      />
      <Button
        isDisabled={!hasActiveFiltersForSubset(defaultParams)}
        onClick={handleResetFilters}
        variant="secondary"
      >
        Reset
      </Button>
    </ButtonGroup>
  );
};
